import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  deleteWebsiteProductApi,
  deleteWebsiteProductFileApi,
  getAllWebsiteProductApi,
  getWebsiteProductApi,
  postWebsiteProductApi,
  postWebsiteProductFilesApi,
  updateWebsiteProductApi,
} from "../../api/common/WebsiteProductApi";

const initialState = {
  websiteProduct: null,
  websiteProducts: [],
  websiteProductFiles: [],
  loading: false,
  detailsLoading: false,
  saving: false,
  deleting: false,
  fileUploading: false,
  fileDeleting: false,
  error: null,
  success: false,
};

const getErrorPayload = (err) => ({
  errorMessage:
    err?.data?.message ||
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    "Something went wrong",
});

export const getWebsiteProduct = createAsyncThunk(
  "websiteProduct/getWebsiteProduct",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getWebsiteProductApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const getAllWebsiteProduct = createAsyncThunk(
  "websiteProduct/getAllWebsiteProduct",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllWebsiteProductApi();
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const postWebsiteProduct = createAsyncThunk(
  "websiteProduct/postWebsiteProduct",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postWebsiteProductApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const updateWebsiteProduct = createAsyncThunk(
  "websiteProduct/updateWebsiteProduct",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateWebsiteProductApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const deleteWebsiteProduct = createAsyncThunk(
  "websiteProduct/deleteWebsiteProduct",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteWebsiteProductApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const postWebsiteProductFiles = createAsyncThunk(
  "websiteProduct/postWebsiteProductFiles",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postWebsiteProductFilesApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const deleteWebsiteProductFile = createAsyncThunk(
  "websiteProduct/deleteWebsiteProductFile",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteWebsiteProductFileApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

const websiteProductSlice = createSlice({
  name: "websiteProduct",
  initialState,
  reducers: {
    clearWebsiteProductError: (state) => {
      state.error = null;
    },
    clearWebsiteProductState: (state) => {
      state.websiteProduct = null;
      state.websiteProducts = [];
      state.websiteProductFiles = [];
      state.loading = false;
      state.detailsLoading = false;
      state.saving = false;
      state.deleting = false;
      state.fileUploading = false;
      state.fileDeleting = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWebsiteProduct.pending, (state) => {
        state.detailsLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getWebsiteProduct.fulfilled, (state, action) => {
        state.websiteProduct = action?.payload?.response ?? action?.payload;
        state.detailsLoading = false;
        state.success = true;
      })
      .addCase(getWebsiteProduct.rejected, (state, action) => {
        state.detailsLoading = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(getAllWebsiteProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getAllWebsiteProduct.fulfilled, (state, action) => {
        state.websiteProducts = action?.payload?.response ?? action?.payload ?? [];
        state.loading = false;
        state.success = true;
      })
      .addCase(getAllWebsiteProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(postWebsiteProduct.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(postWebsiteProduct.fulfilled, (state, action) => {
        state.websiteProduct = action?.payload?.response ?? action?.payload;
        state.saving = false;
        state.success = true;
      })
      .addCase(postWebsiteProduct.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(updateWebsiteProduct.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateWebsiteProduct.fulfilled, (state, action) => {
        state.websiteProduct = action?.payload?.response ?? action?.payload;
        state.saving = false;
        state.success = true;
      })
      .addCase(updateWebsiteProduct.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(deleteWebsiteProduct.pending, (state) => {
        state.deleting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteWebsiteProduct.fulfilled, (state) => {
        state.deleting = false;
        state.success = true;
      })
      .addCase(deleteWebsiteProduct.rejected, (state, action) => {
        state.deleting = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(postWebsiteProductFiles.pending, (state) => {
        state.fileUploading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(postWebsiteProductFiles.fulfilled, (state, action) => {
        state.websiteProductFiles =
          action?.payload?.response ?? action?.payload ?? [];
        state.fileUploading = false;
        state.success = true;
      })
      .addCase(postWebsiteProductFiles.rejected, (state, action) => {
        state.fileUploading = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(deleteWebsiteProductFile.pending, (state) => {
        state.fileDeleting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteWebsiteProductFile.fulfilled, (state) => {
        state.fileDeleting = false;
        state.success = true;
      })
      .addCase(deleteWebsiteProductFile.rejected, (state, action) => {
        state.fileDeleting = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      });
  },
});

export const { clearWebsiteProductError, clearWebsiteProductState } =
  websiteProductSlice.actions;

export default websiteProductSlice.reducer;
